import React, { useMemo, useRef, useState } from 'react';
import useElementWidth from './useElementWidth';
import Tooltip from './Tooltip';
import { niceTicks, isNum } from '../format';

const M = { top: 12, right: 8, bottom: 28, left: 8 };
const GAP = 2;

/**
 * Stacked columns (ordered layers, e.g. permits by building size).
 * layers: [{ id, label, color, values: [number|null] }] — bottom layer first.
 */
export default function StackedColumns({ categories = [], layers = [], height = 200, yFormat = (v) => String(v), labelEvery }) {
  const wrapRef = useRef(null);
  const width = useElementWidth(wrapRef);
  const [hover, setHover] = useState(null);

  const model = useMemo(() => {
    const n = categories.length;
    if (!n || !layers.length) return null;
    const totals = categories.map((_, i) => layers.reduce((sum, l) => sum + (isNum(l.values[i]) ? l.values[i] : 0), 0));
    const yMax = Math.max(...totals, 0);
    const ticks = niceTicks(0, yMax || 1, 4);
    const y1 = ticks[ticks.length - 1];
    const labelWidth = Math.max(...ticks.map((t) => yFormat(t).length), 2) * 7 + 8;
    const left = M.left + labelWidth;
    const plotW = Math.max(40, width - left - M.right);
    const plotH = Math.max(40, height - M.top - M.bottom);
    const band = plotW / n;
    const barW = Math.min(band * 0.7, 24);
    const scale = (v) => (v / (y1 || 1)) * plotH;
    const every = labelEvery || Math.max(1, Math.ceil((n * 56) / plotW));
    return { n, totals, ticks, left, plotW, plotH, band, barW, scale, y1, every };
  }, [categories, layers, width, height, yFormat, labelEvery]);

  if (!model) return <div ref={wrapRef} className="le-chart-empty" style={{ height }}>No data to plot</div>;

  const baseY = M.top + model.plotH;
  const hoverRows = hover !== null ? [
    ...layers.slice().reverse().map((l) => ({ id: l.id, label: l.label, color: l.color, value: isNum(l.values[hover]) ? yFormat(l.values[hover]) : '0' })),
    { id: 'total', label: 'Total', value: yFormat(model.totals[hover]) },
  ] : [];

  return (
    <div ref={wrapRef} className="le-chart le-stacked-chart" style={{ height }} onPointerLeave={() => setHover(null)}>
      <svg width={width} height={height} role="img" aria-label="Stacked column chart">
        {model.ticks.map((t) => (
          <g key={t}>
            <line className="le-grid" x1={model.left} x2={model.left + model.plotW} y1={baseY - model.scale(t)} y2={baseY - model.scale(t)} />
            <text className="le-axis-text" x={model.left - 6} y={baseY - model.scale(t) + 4} textAnchor="end">{yFormat(t)}</text>
          </g>
        ))}
        <line className="le-axis" x1={model.left} x2={model.left + model.plotW} y1={baseY} y2={baseY} />
        {categories.map((cat, i) => {
          const bx = model.left + i * model.band;
          const x = bx + (model.band - model.barW) / 2;
          let acc = 0;
          return (
            <g key={cat} className={hover === i ? 'le-bar-group active' : 'le-bar-group'}>
              <rect className="le-hit" x={bx} y={M.top} width={model.band} height={model.plotH} onPointerEnter={() => setHover(i)} onPointerMove={() => setHover(i)} />
              {layers.map((l, li) => {
                const v = isNum(l.values[i]) ? l.values[i] : 0;
                if (v <= 0) return null;
                const h = model.scale(v);
                const yTop = baseY - model.scale(acc) - h;
                acc += v;
                const isTop = li === layers.length - 1 || layers.slice(li + 1).every((nl) => !(isNum(nl.values[i]) && nl.values[i] > 0));
                const drawH = Math.max(0, h - (isTop ? 0 : GAP));
                return (
                  <rect
                    key={l.id}
                    className="le-bar"
                    x={x}
                    y={yTop}
                    width={model.barW}
                    height={drawH}
                    rx={isTop ? 3 : 0}
                    style={{ fill: l.color }}
                  />
                );
              })}
              {i % model.every === 0 ? (
                <text className="le-axis-text" x={bx + model.band / 2} y={height - 8} textAnchor="middle">{cat}</text>
              ) : null}
            </g>
          );
        })}
      </svg>
      {hover !== null ? (
        <Tooltip x={model.left + hover * model.band + model.band / 2} y={M.top + 10} width={width} title={categories[hover]} rows={hoverRows} />
      ) : null}
    </div>
  );
}
